/**
 * Refresh cadence for queries whose payload contains S3 presigned URLs.
 *
 * Presigned URLs from the Helios media endpoint expire on a
 * server-configured TTL. S3 sig-v4 defaults to 15 minutes and the
 * client has no visibility into the actual value on the wire, so we
 * refresh conservatively at 10 minutes — a 5-minute safety margin
 * below the shortest plausible expiry.
 *
 * This interval is intentionally *below* the URL TTL, not equal to
 * it: a user who left the page inactive for the full TTL would
 * otherwise see 403 images for the interval between the URL expiring
 * and the next scheduled refetch. The 5-minute margin also absorbs
 * the round-trip and clock skew between the client's timer and the
 * URL's actual expiry.
 *
 * Consumers wire this via `createQuery`'s `defaultOptions` argument:
 *
 * ```ts
 * export const useListMediaByIdsQuery = createQuery(
 *   MEDIA_BY_IDS_KEY,
 *   (input) => ...,
 *   {
 *     refetchInterval: MEDIA_URL_REFRESH_INTERVAL_MS,
 *     refetchIntervalInBackground: false,
 *   },
 * );
 * ```
 *
 * `refetchIntervalInBackground: false` (the tanstack-react-query
 * default) is repeated at each call site so hidden tabs don't waste
 * S3 fetches. When a hidden tab returns to the foreground, the
 * default `refetchOnWindowFocus: true` combined with `staleTime`
 * (30 s in MobileShell) will pick up a fresh URL immediately.
 *
 * If we later learn the backend uses a longer TTL (e.g. 1 hour), we
 * can widen the interval here in one place. If we ever move to
 * server-supplied `expiresAt` timestamps, this module can be replaced
 * with a per-query function of the earliest expiry in the cache.
 */
export const MEDIA_URL_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
